import { createContext, useContext, useState, useCallback } from 'react';
import { updatePostHogConsent } from '@/lib/posthog';

export interface CookieConsent {
  necessary: true;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
}

interface CookieConsentContextValue {
  consent: CookieConsent | null;
  hasConsented: boolean;
  acceptAll: () => void;
  declineAll: () => void;
  savePreferences: (prefs: Partial<Omit<CookieConsent, 'necessary' | 'timestamp'>>) => void;
  isAllowed: (category: keyof Omit<CookieConsent, 'timestamp'>) => boolean;
  isSettingsOpen: boolean;
  openSettings: () => void;
  closeSettings: () => void;
}

const STORAGE_KEY = 'cookie-consent';

function getStoredConsent(): CookieConsent | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as CookieConsent;
  } catch {
    return null;
  }
}

function storeConsent(consent: CookieConsent): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
}

export const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export function useCookieConsentProvider() {
  const [consent, setConsent] = useState<CookieConsent | null>(() => getStoredConsent());
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const hasConsented = consent !== null;

  const acceptAll = useCallback(() => {
    const newConsent: CookieConsent = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    storeConsent(newConsent);
    setConsent(newConsent);
    setIsSettingsOpen(false);
    updatePostHogConsent(true);
  }, []);

  const declineAll = useCallback(() => {
    const newConsent: CookieConsent = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    storeConsent(newConsent);
    setConsent(newConsent);
    setIsSettingsOpen(false);
    updatePostHogConsent(false);
  }, []);

  const savePreferences = useCallback((prefs: Partial<Omit<CookieConsent, 'necessary' | 'timestamp'>>) => {
    const newConsent: CookieConsent = {
      necessary: true,
      functional: prefs.functional ?? false,
      analytics: prefs.analytics ?? false,
      marketing: prefs.marketing ?? false,
      timestamp: new Date().toISOString(),
    };
    storeConsent(newConsent);
    setConsent(newConsent);
    setIsSettingsOpen(false);
    updatePostHogConsent(newConsent.analytics);
  }, []);

  const isAllowed = useCallback((category: keyof Omit<CookieConsent, 'timestamp'>) => {
    if (category === 'necessary') return true;
    if (!consent) return false;
    return consent[category];
  }, [consent]);

  const openSettings = useCallback(() => setIsSettingsOpen(true), []);
  const closeSettings = useCallback(() => setIsSettingsOpen(false), []);

  return {
    consent,
    hasConsented,
    acceptAll,
    declineAll,
    savePreferences,
    isAllowed,
    isSettingsOpen,
    openSettings,
    closeSettings,
  };
}

export function useCookieConsent(): CookieConsentContextValue {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}
